import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, shareReplay, Subject, takeUntil } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import {
  CompactType,
  DisplayGrid,
  GridsterConfig,
  GridsterItem,
  GridsterItemComponentInterface,
  GridType
} from 'angular-gridster2';
import { MatDialog } from '@angular/material/dialog';
import { IAppPluginWidget, IPermissionsFlags, IPluginPermissions, ModelService } from '../../models';
import {
  IWidget,
  IWidgetDefaultLayoutConfig,
  IWidgetAddAction
} from './widget.interface';
import { DynamicModuleWidgetLoaderService } from '../dynamic-module-widgets-loader/dynamic-module-widget-loader.service';
import { IModuleWidget } from '../dynamic-module-widgets-loader/dynamic-module-widget-interface';
import { PluginInjectorService } from '../plugin-injector.service';


@Injectable({
  providedIn: 'root'
})
export class WidgetService extends DynamicModuleWidgetLoaderService {
  private defaultLayoutPositions: IWidgetDefaultLayoutConfig = { x: 0, y: 0, cols: 8, rows: 12, width: 0, height: 0 };
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();

  private readonly WidgetsSubject$: BehaviorSubject<IWidget[]> = new BehaviorSubject<IWidget[]>([]);
  widgets$: Observable<IWidget[]> = this.WidgetsSubject$.asObservable().pipe(
    shareReplay(1), // Ensures only one subscription exists for all consumers
    takeUntil(this.ngUnsubscribe$),
    finalize(() => console.log('widgets$ - Observable completed.')
    ));

  // Emitted when new widgetItem added
  add$: Subject<IWidgetAddAction> = new Subject<IWidgetAddAction>();

  // Emitted when widgetItem gets edited
  edit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Emitted when widgetItem was deleted
  delete$: Subject<string> = new Subject<string>();

  // Emitted when widgetItem size was changed
  sizeChanged$: Subject<IAppPluginWidget> = new Subject<IAppPluginWidget>();

  // angular-gridster2 configuration
  options: GridsterConfig = {
    // Grid Types
    gridType: GridType.Fit,
    // Compact
    compactType: CompactType.None,
    // Display Grid
    displayGrid: DisplayGrid.None,
    // Grid Margins Option
    margin: 0.5,
    outerMargin: true,
    outerMarginTop: null,
    outerMarginRight: null,
    outerMarginBottom: null,
    outerMarginLeft: null,
    // Add Items
    pushItems: true,
    // Swap
    swap: false,
    // Grid Sizes
    minCols: 30,
    maxCols: 100,
    minRows: 30,
    maxRows: 100,
    maxItemCols: 100,
    minItemCols: 1,
    maxItemRows: 100,
    minItemRows: 1,
    maxItemArea: 2500,
    minItemArea: 1,
    defaultItemCols: 1,
    defaultItemRows: 1,
    fixedColWidth: 200,
    fixedRowHeight: 200,
    // Drag
    draggable: {
      enabled: false
    },
    // Resize
    resizable: {
      enabled: false
    },
    // Mobile options
    mobileBreakpoint: 640, // Set breakpoint for mobile devices
    keepFixedHeightInMobile: false,
    keepFixedWidthInMobile: false,
    useBodyForBreakpoint: false,
    useTransformPositioning: true,
    itemChangeCallback: (item: GridsterItem, itemComponent: GridsterItemComponentInterface) => {
      const widgetGridsterItem = <IWidget>item
      this.updateGridLayoutConfig(widgetGridsterItem, itemComponent)
    }
  };

  setWidgets(widgets: IWidget[]): void {
    this.WidgetsSubject$.next(widgets);
  }

  unsubscribeWidgets$() {
    this.WidgetsSubject$.complete();

    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  getWidgets(): IWidget[] {
    return this.WidgetsSubject$.getValue();
  }

  addWidgetItem(widget: IWidget): void {
    const currentWidgets = this.getWidgets();
    currentWidgets.push(widget);
    // inform subscribers about changes
    this.WidgetsSubject$.next(currentWidgets);
  }

  getWidgetItem(_id: string): IWidget | undefined {
    return this.getWidgets().find((item: IWidget) => item._id === _id);
  }

  removeWidgetItemById(_id: string): void {
    const currentWidget = this.getWidgets();

    currentWidget.splice(currentWidget.findIndex((item: IWidget) => item._id === _id), 1);

    // emit delete widget
    this.delete$.next(_id);
    // emit updated widget
    this.WidgetsSubject$.next(currentWidget);
  }

  removeWidgetByItem(widget: IWidget): void {
    const currentWidgets = this.getWidgets();

    currentWidgets.splice(currentWidgets.indexOf(widget), 1);
    // emit delete widget
    this.delete$.next(widget._id);
    // emit updated widget
    this.WidgetsSubject$.next(currentWidgets);
  }

  async executeAdditionalPluginAction(widget: IWidget, modelService: ModelService, data: any): Promise<void> {
    if (widget.pluginModule === 'MAP') {
      if (data && data.length > 0) {
        modelService._plugin.executeAdditionalPluginAction$(
            widget.pluginId,
            widget.pluginModule,
            'delete_ngsi_subscription_widget',
            data
        );
      }
    }
  }

  getDefaultLayoutConfig(): IWidgetDefaultLayoutConfig {
    return this.defaultLayoutPositions;
  }

  private updateGridLayoutConfig(widget: IWidget, itemComponent: GridsterItemComponentInterface): void {
    const widgetItem = this.getWidgetItem(widget._id);
    if (widgetItem) {
      widgetItem.x = widget.x;
      widgetItem.y = widget.y;
      widgetItem.cols = widget.cols;
      widgetItem.rows = widget.rows;
      widgetItem.width = itemComponent.width ? itemComponent.width : widget.width;
      widgetItem.height = itemComponent.height ? itemComponent.height : widget.height;
      ;
      const appPluginWidget = widgetItem.appPluginWidget;
      appPluginWidget.layout.position_x = widgetItem.x;
      appPluginWidget.layout.position_y = widgetItem.y;
      appPluginWidget.layout.columns = widgetItem.cols;
      appPluginWidget.layout.rows = widgetItem.rows;
      appPluginWidget.layout.width_px = widgetItem.width;
      appPluginWidget.layout.height_px = widgetItem.height;

      this.sizeChanged$.next(appPluginWidget);
    }
  }

  async loadSettingComponent(widget: IWidget, modelService: ModelService, pluginInjectorService: PluginInjectorService, dialog: MatDialog): Promise<void> {
    const widgetComponents = this.getModuleWidgets()
    .find((widgetComponent: IModuleWidget) => widgetComponent.settingComponent &&
        widgetComponent.pluginId === widget.pluginId &&
        widgetComponent.pluginModule === widget.pluginModule
    );

    if (widgetComponents?.settingComponent) {
      await this.openDynamicDialogComponent(
          widget,
          widgetComponents.settingComponent,
          modelService,
          pluginInjectorService,
          dialog
      );
    }
  }

  isConfigurable(widget: IWidget): boolean {
    return this.getModuleWidgets().some((widgetComponent: IModuleWidget) =>
        widgetComponent.settingComponent && widgetComponent.pluginId === widget.pluginId &&
        widgetComponent.pluginModule === widget.pluginModule
    );
  }

  showWidgetTitle(widget: IWidget): boolean {
    return widget.appPluginWidget.settings.showWidgetTitle;
  }

  widgetSettingsEnabled(widget: IWidget, pluginPermissions: IPluginPermissions): boolean {
    if (pluginPermissions?.widget_permissions && widget.widgetPermissions?.widgetBasePermissions?.widgetSettingsEnabledPermission) {
      const widgetPermissions = pluginPermissions.widget_permissions;
      const widgetPermissionsKeys = Object.keys(widgetPermissions);
      const firstPermissionsKey = widgetPermissionsKeys.length ? widgetPermissionsKeys[0] : null;
      if (firstPermissionsKey != null && widgetPermissions[firstPermissionsKey] != null) {
        if (typeof widgetPermissions[firstPermissionsKey] === 'boolean') {
          return widgetPermissions[widget.widgetPermissions.widgetBasePermissions.widgetSettingsEnabledPermission.permissionRef] as boolean
          // if first key of the widget_permissions object refers to a plugin module name and not to a permission flag, the key's value will be a nested object which holds the permission flags instead
        } else if (typeof widgetPermissions[firstPermissionsKey] === 'object') {
          const permissionKey = widgetPermissionsKeys.find(key => key.toLowerCase() === widget.pluginModule.toLowerCase());
          if (permissionKey != null) {
            return (widgetPermissions[permissionKey] as IPermissionsFlags)[widget.widgetPermissions.widgetBasePermissions.widgetSettingsEnabledPermission.permissionRef] as boolean
          }
        }
      }
    }
    return false;
  }

  showWidgetHeader(widget: IWidget, pluginPermissions: IPluginPermissions): boolean {
    if (pluginPermissions?.widget_permissions && widget.widgetPermissions?.widgetBasePermissions?.autorizeDisplayOfWidgetHeader) {
      const widgetPermissions = pluginPermissions.widget_permissions;
      const widgetPermissionsKeys = Object.keys(widgetPermissions);
      const firstPermissionsKey = widgetPermissionsKeys.length ? widgetPermissionsKeys[0] : null;
      if (firstPermissionsKey != null && widgetPermissions[firstPermissionsKey] != null) {
        if (typeof widgetPermissions[firstPermissionsKey] === 'boolean') {
          return widgetPermissions[widget.widgetPermissions.widgetBasePermissions.autorizeDisplayOfWidgetHeader.permissionRef] as boolean
        // if first key of the widget_permissions object refers to a plugin module name and not to a permission flag, the key's value will be a nested object which holds the permission flags instead
        } else if (typeof widgetPermissions[firstPermissionsKey] === 'object') {
          const permissionKey = widgetPermissionsKeys.find(key => key.toLowerCase() === widget.pluginModule.toLowerCase());
          if (permissionKey != null) {
            return (widgetPermissions[permissionKey] as IPermissionsFlags)[widget.widgetPermissions.widgetBasePermissions.autorizeDisplayOfWidgetHeader.permissionRef] as boolean
          }
        }
      }
    }
    return false
  }

  editActionEnabled(widget: IWidget, pluginPermissions: IPluginPermissions): boolean {
    if (pluginPermissions?.widget_permissions && widget.widgetPermissions?.widgetAdditionalPermissions?.authorizeEditAction) {
      return pluginPermissions.widget_permissions[widget.widgetPermissions.widgetAdditionalPermissions.authorizeEditAction.permissionRef] as boolean
    } else {
      return false
    }
  }

  deleteActionEnabled(widget: IWidget, pluginPermissions: IPluginPermissions): boolean {
    if (pluginPermissions?.widget_permissions && widget.widgetPermissions?.widgetAdditionalPermissions?.authorizeDeleteAction) {
      return pluginPermissions.widget_permissions[widget.widgetPermissions.widgetAdditionalPermissions.authorizeDeleteAction.permissionRef] as boolean
    } else {
      return false
    }
  }

  addActionEnabled(widget: IWidget, pluginPermissions: IPluginPermissions): boolean {
    if (pluginPermissions?.widget_permissions && widget.widgetPermissions?.widgetAdditionalPermissions?.authorizeCreateAction) {
      return pluginPermissions.widget_permissions[widget.widgetPermissions.widgetAdditionalPermissions.authorizeCreateAction.permissionRef] as boolean
    } else {
      return false
    }
  }

  searchActionEnabled(widget: IWidget, pluginPermissions: IPluginPermissions): boolean {
    if (pluginPermissions?.widget_permissions && widget.widgetPermissions?.widgetAdditionalPermissions?.authorizeSearchAction) {
      return pluginPermissions.widget_permissions[widget.widgetPermissions.widgetAdditionalPermissions.authorizeSearchAction.permissionRef] as boolean
    } else {
      return false
    }
  }

  fullScreenActionEnabled(widget: IWidget, pluginPermissions: IPluginPermissions): boolean {
    if (pluginPermissions?.widget_permissions && widget.widgetPermissions?.widgetAdditionalPermissions?.authorizeFullscreenAction) {
      return pluginPermissions.widget_permissions[widget.widgetPermissions.widgetAdditionalPermissions.authorizeFullscreenAction.permissionRef] as boolean
    } else {
      return false
    }
  }
}
